import * as analytics from 'analytics/Analytics';
import {ActionMethod, ActionOrigin, ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ColumnDataType, guessColumnDataType} from 'notebook/structured-tables/columns/ColumnDataType';
import {Table} from 'notebook/tables/constants/TableConstants';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {FullTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {ReorderManualOptions} from 'notebook/tables/excess/ops/TableOps';
import TableUtil from 'notebook/tables/TableUtil';
import {FileAnalyticsParams} from 'notebook/util/analytics/FileAnalyticsUtils';
import {Note} from 'notebook/util/NoteUtilConstants';

/**
 * Returns the total cell count of cells in the entry that are evaluated during the main evaluation lifecycle.
 *
 * If the note does not have an evaluation snapshot, null is returned.
 */
export function getNumberOfEvaluableCellsInNote(note: Note): number | null {
  let result: number | null = null;
  if (note.noteRuntimeSnapshot != null && note.noteRuntimeSnapshot.evaluationSnapshot != null) {
    result = 0;
    Object.values(note.noteRuntimeSnapshot.evaluationSnapshot).forEach((table) => {
      if (table.length > 0) {
        result! += table.length * table[0].length;
      }
    });
  }
  return result;
}

/**
 * Returns the total cell count for the specified FullTableMap. This is theoretically the same as would be
 * returned by getNumberOfEvaluableCellsInNote for the same entry.
 */
export function getNumberOfTableCellsInTableMap(tableMap: FullTableMap): number {
  let result = 0;
  Object.values(tableMap).forEach((table) => {
    if (table.table.length > 0) {
      result += table.table.length * table.table[0].length;
    }
  });
  return result;
}

export function tableCellCountBucket(cellCount: number): string {
  if (cellCount <= 1000) {
    return '0-1K';
  } else if (cellCount <= 10000) {
    return '1K-10K';
  } else if (cellCount <= 100000) {
    return '10K-100K';
  } else if (cellCount <= 1000000) {
    return '100K-1M';
  } else {
    return '1M+';
  }
}

export function trackDragToReorderRowsOrColumns(
  table: Table,
  reorderOptions: ReorderManualOptions,
  fileAnalyticsParams: FileAnalyticsParams,
  tableViewAnalyticsParams: {
    isMaximized: boolean;
    isFixedHeight: boolean;
    isSplitScreen: boolean;
  }
): void {
  const tableMetadata = TableUtil.getTableMetadataFromStructuredTable(table);
  const structuredTableMetadata = TableUtil.getStructuredTableMetadataFromTable(table);

  // Only track column types if we are reordering columns
  const columnTypes: Array<ColumnDataType | null> =
    reorderOptions.dim === TableDimension.COLUMN
      ? table.columnConstraints.map(
          (columnConstraint) =>
            (columnConstraint?.columnDefinition &&
              tableMetadata &&
              guessColumnDataType(columnConstraint.columnDefinition, tableMetadata)?.type) ??
            null
        )
      : [];

  analytics.trackEvent(ELN_ANALYTICS_ACTIONS.REORDERED_TABLE_DIMENSION, {
    actionOrigin: structuredTableMetadata
      ? ActionOrigin.NOTEBOOK_UNDETERMINED_STRUCTURED_TABLE
      : ActionOrigin.NOTEBOOK_PLAIN_TABLE,
    actionMethod: ActionMethod.STRUCTURED_TABLE_AXIS_DRAG_HANDLE,
    ...fileAnalyticsParams,
    // Structured table information
    is_config: tableMetadata?.isConfig,
    structured_table_api_identifier: tableMetadata?.apiId ?? null,
    table_provenance_uuid: tableMetadata?.provenanceUuid,
    structured_table_kind: tableMetadata?.kind,
    structured_table_mode: (structuredTableMetadata as {mode?: string} | null)?.mode,
    structured_table_metadata: structuredTableMetadata ? JSON.stringify(structuredTableMetadata) : null,
    // Table information
    table_num_rows: table.table.length,
    table_num_columns: table.table[0]?.length,
    table_locked_dims: table.lockedDims == null ? null : JSON.stringify(table.lockedDims),
    table_column_types: columnTypes,
    ...tableViewAnalyticsParams,
    // Reordering information
    start_index: reorderOptions.startIndex,
    end_index: reorderOptions.endIndex,
    insert_index: reorderOptions.insertPosition,
    dimension: reorderOptions.dim,
  });
}
